import { useEffect, useMemo, useState, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_PATIENTS } from 'pages/SchedulePage/gqlSchemes/getPatients';
import { PageInfo, PatientUser } from '../../../../generated/graphql';

const useLoadPatients = (searchPatients: string, careTeamIds?: string[], shouldUseCareTeamFilter = false, loadOnMount = true) => {
  const [patients, setPatients] = useState<PatientUser[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo>({} as PageInfo);
  const [callCount, setCallCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const lastCallParamsRef = useRef<string>('');
  const [hasBeenTriggered, setHasBeenTriggered] = useState(loadOnMount);

  const [getPatients, {
    loading: patientsLoading,
    client
  }] = useLazyQuery(GET_PATIENTS, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const patientsData = data?.user?.patient?.patientPagination?.items as PatientUser[];
      setPatients(patientsData);
      setPageInfo({
        ...data?.user?.patient?.patientPagination?.pageInfo as PageInfo
      });
      setCallCount(callCount + 1)
    }
  });

  // Manual trigger function for on-demand loading
  const triggerLoad = () => {
    console.log('🚀 useLoadPatients - triggerLoad called');
    setHasBeenTriggered(true);
  };

  const performFetch = () => {
    console.log('🔥 useLoadPatients - performFetch called', { 
      searchPatients, 
      careTeamIds, 
      shouldUseCareTeamFilter,
      hasBeenTriggered 
    });
    
    // Only block if filtering is required but no care team IDs provided
    // This allows free search when not filtering
    if (shouldUseCareTeamFilter && (!careTeamIds || careTeamIds.length === 0)) {
      console.log('⛔ useLoadPatients - Blocked: shouldUseCareTeamFilter but no careTeamIds');
      setPatients([]); // Clear results when filtering required but no filter available
      return;
    }
    
    // Create a unique key for this call parameters
    const callKey = `${searchPatients}|${JSON.stringify(careTeamIds || [])}|${shouldUseCareTeamFilter}`;
    
    // Prevent duplicate calls with same parameters
    if (lastCallParamsRef.current === callKey) {
      console.log('⛔ useLoadPatients - Blocked: duplicate call with same params');
      return;
    }
    
    lastCallParamsRef.current = callKey;
    setCurrentPage(0); // Reset pagination for new search
    
    console.log('✅ useLoadPatients - Fetching patients with:', {
      page: 0,
      perPage: 20,
      withCarePlan: true,
      filter: {
        name: searchPatients,
        ...(careTeamIds && careTeamIds.length > 0 && { careTeamIds }),
      }
    });
    
    getPatients({
      variables: {
        page: 0,
        perPage: 20,
        withCarePlan: true,
        filter: {
          name: searchPatients,
          ...(careTeamIds && careTeamIds.length > 0 && { careTeamIds }),
        }
      }
    });
  };

  // Auto-load logic - more predictable and responsive
  useEffect(() => {
    // Always trigger search when:
    // 1. loadOnMount is true (appointment dialog)
    // 2. hasBeenTriggered is true (medical task dialog after manual trigger)
    // 3. searchPatients has content (user is actively searching)
    if (hasBeenTriggered || searchPatients.length > 0) {
      performFetch();
    }
  }, [searchPatients, JSON.stringify(careTeamIds), hasBeenTriggered]);

  const queryForPagination = {
    query: GET_PATIENTS,
    fetchPolicy: "no-cache" as 'no-cache',
    variables: {
      page: currentPage + 1,
      perPage: 20,
      withCarePlan: true,
      filter : {
        name: searchPatients,
        ...(careTeamIds && careTeamIds.length > 0 && { careTeamIds }),
      },
    },
  };

  const fetchData = (searchPatients: string) => {
    // Only allow pagination if initial fetch was allowed
    if (shouldUseCareTeamFilter && (!careTeamIds || careTeamIds.length === 0)) {
      return;
    }

    client?.query(queryForPagination)
      .then((res) => {
        setPatients(patients?.concat(res.data?.user?.patient?.patientPagination?.items));
        setPageInfo(res.data?.user?.patient?.patientPagination?.pageInfo);
        setCurrentPage((currentPage) => currentPage + 1)
      })
  };

  return {
    patients,
    setPatients,
    pageInfo,
    fetchData,
    patientsLoading,
    callCount,
    triggerLoad, // New function to manually trigger loading
  }
};

export default useLoadPatients;


//  const [patients, setPatients] = useState('');
// const [pageNumber, setPageNumber] = useState(0);
// const [paginationInfo, setPaginationInfo] = useState(null);
// const [rowsPerPage, setRowsPerPage] = useState(10);

// const [getPatients, { loading: loadingGetPatients, data }] = useLazyQuery(
//   GET_PATIENTS,
//   {
//     fetchPolicy: 'no-cache',
//     variables: {
//       page: pageNumber,
//       perPage: rowsPerPage,
//       withCarePlan: true,
//       filter: {
//         name: filter
//       },
//     },
//   }
// );

// useEffect(() => {
//   getPatients();
// },[])

// useEffect(() => {
//   if(searchPatients === ''){
//     setPatients('');
//     setPaginationInfo(null);
//   } else {
//     getPatients();
//   }
// }, [getPatients, searchPatients]);

// useEffect(() => {
//   if (data) {
//     setPatients(data?.user?.patient?.patientPagination?.items);
//     setPaginationInfo(data?.user?.patient?.patientPagination?.pageInfo);
//   }
// }, [data]);